import { TENANT } from '../../config';

/**
 * Securities / risk disclaimer for the auth flow.
 *
 * Single source of truth for the copy so the desktop page footer and the mobile in-form
 * placement never drift. Pass layout/spacing via `className` (the caller controls where and
 * when it shows — e.g. `md:hidden` for the mobile-only instance).
 *
 * `text-wrap: balance` + a non-breaking space in "Regulation D." keep the line from
 * orphaning the trailing "D." onto its own line.
 *
 * Which copy appears is decided by what the tenant SELLS, not by which build
 * produced the bundle. The securities sentence used to be the `else` of a
 * `REACT_APP_TENANT_ID === 'redex'` check, so every tenant that was not REDEX
 * inherited it — and eMerge, whose users are hearing-care staff signing in to
 * see residents, was telling them on the sign-in page that it offers securities
 * under Regulation D. A build id is not a capability. `features.investing` is,
 * and it is what the settings registry and the register route already gate on.
 *
 * A tenant that sells no securities renders nothing here. That is deliberate:
 * the honest alternative to a false disclaimer is silence, not a substitute
 * disclaimer written by whoever happened to be fixing the bug.
 */
export function SecuritiesDisclaimer({ className = '' }: { className?: string }) {
  const isRedexBuild = String(process.env.REACT_APP_TENANT_ID || '').trim().toLowerCase() === 'redex';
  const platformName = TENANT.branding.name;

  if (isRedexBuild) {
    return (
      <div className={`text-center text-xs text-muted-foreground space-y-2 [text-wrap:balance] ${className}`}>
        <p>
          Platform usage involves risk.
          <br />
          Always verify transaction details before approving.
        </p>
      </div>
    );
  }

  if (!TENANT.features.investing) return null;

  return (
    <div className={`text-center text-xs text-muted-foreground space-y-2 [text-wrap:balance] ${className}`}>
      <p>
        Securities offered through {platformName} under Regulation&nbsp;D.
        <br />
        Real estate investments involve risk.
      </p>
    </div>
  );
}
